import { useCallback } from 'react'
import {
  useSaveExplorerConfiguration,
  useAssignClientExplorerConfiguration
} from '../../../../api/coreData'

/**
 * Encapsulates the two-step "save then assign" mutation chain used by
 * the SaveViewForClientDialog. Exposes:
 *   - `saveAndAssign({ name, makeDefault, serializeConfig })`: returns
 *     a promise resolving to `{ explorerConfigurationId, name }` on
 *     success, or rejecting on either step's failure. When the assign
 *     step fails the saved (global) configuration is left in place so
 *     the user can recover from the picker without re-typing the name.
 *   - `isWorking`: true while either mutation is in flight.
 *
 * Lifted out of the component so the chain is unit-testable without a
 * full React render harness.
 */
export function useSaveViewForClient (clientId) {
  const saveMutation = useSaveExplorerConfiguration()
  const assignMutation = useAssignClientExplorerConfiguration()

  const saveAndAssign = useCallback(async ({ name, makeDefault, serializeConfig }) => {
    if (!clientId) throw new Error('useSaveViewForClient: clientId is required')
    const trimmed = (name || '').trim()
    if (!trimmed) throw new Error('useSaveViewForClient: name is required')

    const serialized = serializeConfig()
    const saveResult = await saveMutation.mutateAsync({
      name: trimmed,
      ...serialized,
      isDefault: false
    })
    const explorerConfigurationId = saveResult?.explorerConfigurationId
    if (!explorerConfigurationId) {
      throw new Error('useSaveViewForClient: save returned no explorerConfigurationId')
    }

    await assignMutation.mutateAsync({
      explorerConfigurationId,
      clientId,
      ...(makeDefault ? { ordinal: 0 } : {})
    })

    return { explorerConfigurationId, name: trimmed }
  }, [clientId, saveMutation, assignMutation])

  return {
    saveAndAssign,
    isWorking: saveMutation.isLoading || assignMutation.isLoading
  }
}
